<template>
  <RemotePaginatedList
    :filter-options="filterOptions"
    :headers="headers"
    multi-sort
    v-model="selectedItemIds"
    data-url="admin/sor/"
    data-key="sors"
    :extra-filters="{
      type: [SOR_TYPE.SOR, SOR_TYPE.CORRIGENDUM],
      hidden: containHiddenPermission ? undefined : [false],
    }"
    :refresh-trigger.sync="innerRefreshTrigger"
    v-bind="$attrs"
    class="sor-list"
  >
    <template #item.type="{ value }">
      {{ $t(`sor_type.${value}`) }}
    </template>
    <template #item.finalised_at="{ value }">
      {{ value ? $parseDate(value) : "" }}
    </template>
    <template #item.action_input_finalised_at="{ value }">
      {{ value ? $parseDate(value) : "" }}
    </template>
    <template v-slot:item.actions="{ item }">
      <div class="d-flex">
        <v-btn text color="primary" @click="onItemClick(item)"> More </v-btn>
        <v-btn
          text
          color="primary"
          v-if="$store.getters.hasPermission(PERMISSIONS.MANAGE_SOR)"
          @click="deleteItem(item)"
        >
          Delete
        </v-btn>
      </div>
    </template>
    <template v-slot:item.hide="{ item }">
      <div class="d-flex">
        <v-checkbox
          v-model="item.hidden"
          @change="() => editHidden(item)"
          :disabled="!containHiddenPermission"
        ></v-checkbox>
      </div>
    </template>
  </RemotePaginatedList>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import RemotePaginatedList from "../RemotePaginatedList";
import _ from "lodash";
import { ACTION_TYPES } from "@/store/types";
import { PERMISSIONS, SOR_TYPE, USER_TYPE } from "@/constants";

export default {
  name: "SorList",
  components: { RemotePaginatedList },
  props: {
    value: Array,
    refreshTrigger: Boolean,
  },
  setup(props, { root, emit }) {
    const refresh = function () {
      innerRefreshTrigger.value = true;
    };

    const containHiddenPermission = computed(() => {
      if (!root.$store.getters.account) return false;

      return (
        [USER_TYPE.SUPER_ADMIN, USER_TYPE.ADMIN].includes(
          root.$store.getters.account.type
        ) || root.$store.getters.hasPermission(PERMISSIONS.MANAGE_SOR, false)
      );
    });

    onMounted(() => {
      refresh(false);
    });

    watch(
      () => props.refreshTrigger,
      (newValue) => {
        if (newValue) {
          refresh(true);
          emit("update:refresh-trigger", false);
        }
      }
    );

    // Filter
    const filterOptions = ref([
      { text: root.$t("sor.edition"), value: "edition.id", contains: true },
      // {text: root.$t('sor.name'), value: 'name', contains: true},
      // {
      //   text: root.$t('sor.finalised'), value: 'finalised_at',
      //   single: true, options: [
      //     {text: 'YES', value: true},
      //     {text: 'NO', value: false},
      //   ],
      // },
      // {
      //   text: root.$t('sor.type'), value: 'type',
      //   options: [
      //     {text: root.$t('sor_type.SOR'), value: 'SOR'},
      //     {text: root.$t('sor_type.CORRIGENDUM'), value: 'CORRIGENDUM'},
      //   ],
      // },
    ]);

    // Data Table
    const headers = computed(() => {
      const header_list = [
        { text: root.$t("sor.edition"), value: "edition.id" },
        { text: root.$t("sor.type"), value: "type", width: 100 },
        { text: root.$t("sor.name"), value: "name", sortable: false },
        {
          text: root.$t("sor.finalised_at"),
          value: "finalised_at",
          width: 150,
        },
        {
          text: root.$t("sor.action_input_finalised_at"),
          value: "action_input_finalised_at",
          width: 150,
        },
        { text: "", value: "actions", sortable: false },
      ];
      if (containHiddenPermission.value) {
        header_list.push({ text: "Hide", value: "hide", sortable: false });
      }

      return header_list;
    });

    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    watch(
      () => selectedItemIds.value,
      (newValue) => {
        emit("input", newValue);
      }
    );
    watch(
      () => props.value,
      (newValue, oldValue) => {
        if (_.xor(newValue, oldValue).length) {
          selectedItemIds.value = newValue;
        }
      },
      { deep: true, immediate: true }
    );

    const editItem = async function (item) {
      root.$router.push({ name: "sor-detail", params: { sorId: item.id } });
    };
    const exportPdf = async function (item) {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/section/export_pdf/",
        params: {
          section_id: item.id,
        },
        opt: { extra: { responseType: "arraybuffer" } },
      });

      let blob = new Blob([response.data], {
        type: response.headers.get("content-type"),
      });
      let link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${item.name}.pdf`;
      link.click();
    };
    const deleteItem = async function (item) {
      if (await root.$dialog.confirm({ text: "Delete SoR?" })) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/sor/delete/",
          params: { sor_id: item.id },
        });

        if (response.body.success) {
          await refresh();
        }
      }
    };

    const onItemClick = function (item) {
      if (item.type === SOR_TYPE.CORRIGENDUM) {
        root.$router.push({
          name: "corrigendum-detail",
          params: { corrigendumId: item.id },
        });
      } else {
        root.$router.push({ name: "sor-detail", params: { sorId: item.id } });
      }
    };

    const editHidden = async function (item) {
      if (containHiddenPermission.value) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/sor/hide/",
          params: {
            sor_id: item.id,
            is_hidden: item.hidden,
          },
        });

        if (response.body.success) {
          await refresh();
        }
      }
      console.log(item.hidden);
    };

    return {
      filterOptions,

      innerRefreshTrigger,
      selectedItemIds,

      editItem,
      exportPdf,
      deleteItem,

      onItemClick,

      SOR_TYPE,
      PERMISSIONS,
      editHidden,
      headers,
      containHiddenPermission,
    };
  },
};
</script>

<style lang="less">
.sor-list {
}
</style>
